import { WahlFeld } from "../design/WahlFeld";
import { Check, Plus, Quote, RefreshCw, Trash2 } from "lucide-react";
import { createElement, useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode, type SelectHTMLAttributes } from "react";
import { useAkten, useBestaetigen, useKategorien, usePersonen } from "../api/abfragen";
import { vorschlagGeaendert } from "../api/client";
import { useNamen } from "../api/namen";
import type { DokumentDetail, Feld as KiFeld, Fristart, GeaendertFeld, RateTurnus, Vorschlag, VorschlagGeaendert } from "../api/typen";
import { Chip } from "../design/Badge";
import { DatumFeld, Feld } from "../design/Feld";
import { Kachel } from "../design/Kachel";
import { kategorieSymbol } from "../design/kategorieSymbole";
import { useWeltAktiv } from "../welt/WeltWurzel";
import { Knopf } from "../design/Knopf";
import { Schalter } from "../design/Schalter";
import { Segment } from "../design/Segment";
import { Fehlertext } from "../design/Leer";
import { Sheet } from "../design/Sheet";
import { useSichern, type SichernLauf } from "../design/sichern";
import { FRISTWORT } from "../welt/saetze";
import { AKTENARTEN, betrag as betragText, betragEingabe, datum as datumText, DOKUMENTART_MAX, FRISTARTEN, hoechstens, rateText, TURNUS_JE, zeichen } from "../format";
import { useAbgelegtMelden, zahlungsbetragVorschau } from "./ablegen";
import { SPERRE_MS, VorschlagGeaendertHinweis } from "./VorschlagGeaendertHinweis";
import {
  abgeleitetBasis,
  aenderungen,
  akteEntspricht,
  akteUebernehmen,
  akteVorbelegung,
  akteWiederOeffnen,
  datumFeld,
  fristWaehlbar,
  grundHalbsatz,
  kartenZitat,
  GRUND_RATENPLAN,
  GRUND_WIEDER_OEFFNEN,
  ratenplanAngebot,
  ratenplanKnopf,
  ratenplanSatz,
  rateSicher,
  rateVollstaendig,
  sicherheit,
  STANDARD_SCHWELLE,
  type AkteForm,
} from "./vorschlag";

/** Auswahl „Gemeinsam“ im Feld „Für wen“: keine Person-ID, sondern ein eigenes Kennzeichen (D-051) */
const GEMEINSAM = "gemeinsam";

/** Sicher ist ein Wert erst ab der Schwelle des Vorschlags. Nur sichere Werte werden vorbelegt;
 *  unsichere erscheinen als markierter Vorschlag zum Uebernehmen (P4: nie still raten). */
function sicher(f: KiFeld | undefined, schwelle: number): boolean {
  return Boolean(f && f.wert !== null && f.konfidenz >= schwelle);
}
function unsicher(f: KiFeld | undefined, schwelle: number): boolean {
  return Boolean(f && f.wert !== null && f.konfidenz > 0 && f.konfidenz < schwelle);
}
/** Woher der Wert kommt (P3): die Textstelle aus dem Brief. */
function Zitat(props: { beleg: string | null | undefined }) {
  if (!props.beleg) return null;
  return (
    <span className="flex gap-1 text-pg-text-2">
      <Quote size={12} strokeWidth={2} aria-hidden className="mt-1 shrink-0" />
      {/* min-w-0: sonst schrumpft das Flex-Kind nicht unter sein laengstes Wort, und „Zahlungsaufforderung“ ragt bei 200 %
          aus der Zeile (Verifier W38-3 R1, A3) */}
      <span className="min-w-0 break-words">{props.beleg}</span>
    </span>
  );
}

/** Hinweis unter einem Feld: Beleg des gewaehlten Vorschlags, oder der unsichere Vorschlag mit Grund
 *  und „Uebernehmen“, oder der Grund, warum nichts vorgeschlagen wird. Hat der Nutzer etwas anderes
 *  gewaehlt, steht kein Beleg mehr darunter, der zu seiner Wahl nicht gehoert (Runde 2, F-K1). */
function Hinweis(props: {
  feld?: KiFeld;
  schwelle: number;
  anzeige?: string;
  uebernommen: boolean;
  /** Das Feld ist leer (nichts gewaehlt oder eingetragen). */
  leer: boolean;
  onUebernehmen?: () => void;
}) {
  const f = props.feld;
  if (!f) return null;
  const vorhanden = sicher(f, props.schwelle) || unsicher(f, props.schwelle);
  if (vorhanden && props.uebernommen) return <Zitat beleg={f.beleg} />;
  if (vorhanden && !props.leer) {
    return (
      <span className="flex flex-wrap items-center gap-x-2 text-pg-text-2">
        <span>Eigene Angabe statt Vorschlag „{props.anzeige ?? f.wert}“</span>
        {props.onUebernehmen && (
          <button type="button" onClick={props.onUebernehmen} className="min-h-(--touch) font-semibold text-pg-accent-text hover:opacity-70">
            Vorschlag nehmen
          </button>
        )}
      </span>
    );
  }
  if (sicher(f, props.schwelle)) {
    return (
      <span className="flex flex-col gap-1">
        <span className="flex flex-wrap items-center gap-x-2 text-pg-text-2">
          <span>Vorschlag „{props.anzeige ?? f.wert}“</span>
          {props.onUebernehmen && (
            <button type="button" onClick={props.onUebernehmen} className="min-h-(--touch) font-semibold text-pg-accent-text hover:opacity-70">
              Übernehmen
            </button>
          )}
        </span>
        <Zitat beleg={f.beleg} />
      </span>
    );
  }
  if (unsicher(f, props.schwelle)) {
    return (
      <span className="flex flex-col gap-1">
        <span className="flex flex-wrap items-center gap-x-2 text-pg-warn">
          <span>
            Vorschlag „{props.anzeige ?? f.wert}“, {sicherheit(f.konfidenz, f.grund, props.schwelle)}
          </span>
          {props.onUebernehmen && (
            <button type="button" onClick={props.onUebernehmen} className="min-h-(--touch) font-semibold text-pg-accent-text hover:opacity-70">
              Übernehmen
            </button>
          )}
        </span>
        <Zitat beleg={f.beleg} />
      </span>
    );
  }
  return f.grund ? <span className="text-pg-warn">{f.grund}</span> : null;
}

/** Pfeiltasten in einer Chip-Gruppe (role=radiogroup, Muster „Radio Group“ der WAI-ARIA APG): links
 *  und oben waehlen den vorigen Chip, rechts und unten den naechsten (rundum), Pos1 und Ende den ersten
 *  bzw. letzten; Auswahl und Fokus wandern zusammen. Dazu traegt nur der gewaehlte Chip tabIndex 0
 *  (Roving Tabindex, siehe `chipTab`), Tab springt so aus der Gruppe heraus. */
function chipTaste(e: KeyboardEvent<HTMLDivElement>) {
  const chips = [...e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]')];
  const jetzt = chips.findIndex((c) => c === document.activeElement);
  const n = chips.length;
  const tasten: Record<string, number | undefined> = { ArrowRight: jetzt + 1, ArrowDown: jetzt + 1, ArrowLeft: jetzt - 1, ArrowUp: jetzt - 1, Home: 0, End: n - 1 };
  const ziel = tasten[e.key];
  if (jetzt < 0 || ziel === undefined) return;
  e.preventDefault();
  const chip = chips[(ziel + n) % n];
  chip?.focus();
  chip?.click();
}

/** tabIndex eines Chips: der gewaehlte, und ist keiner gewaehlt, der erste (sonst waere die Gruppe per
 *  Tab nicht erreichbar). */
function chipTab(aktiv: boolean, keiner: boolean, index: number): 0 | -1 {
  return aktiv || (keiner && index === 0) ? 0 : -1;
}

/** Auswahlfeld wie `Feld`: Beschriftung ueber dem umrandeten Feld, Hinweis darunter, alles in einer Zeile der Gruppe
 *  (W38-3; wie „Angaben ändern“, nie ein Absatz unter der Gruppe, W47e). */
function Auswahl(props: SelectHTMLAttributes<HTMLSelectElement> & { label: string; hinweis?: ReactNode; children: ReactNode }) {
  const { label, hinweis, children, "aria-describedby": eigen, ...rest } = props;
  const id = useId();
  // W52b (W38-3 R1 H1): der Hinweis ist die Beschreibung der Auswahl, wie bei `Feld` (ein eigenes aria-describedby vorne)
  const hinweisId = useId();
  return (
    <li className="zeile zeile-feld relative flex flex-col justify-center gap-2 px-4 pt-1 pb-2">
      <label htmlFor={id} className="t-caption-1 t-caption-1-medium text-pg-text-2">
        {label}
      </label>
      <WahlFeld
        label={label}
        id={id}
        className="t-body min-h-(--feld-h) w-full rounded-md bg-pg-surface px-3 text-pg-text shadow-(--pg-feld-rand) outline-none transition-shadow duration-(--pg-dur-fast) focus-visible:shadow-(--pg-feld-fokus) aria-invalid:shadow-(--pg-feld-fehler)"
        aria-describedby={[eigen, hinweis ? hinweisId : undefined].filter(Boolean).join(" ") || undefined}
        {...rest}
      >
        {children}
      </WahlFeld>
      {hinweis && (
        <span id={hinweisId} className="t-footnote text-pg-text-2">
          {hinweis}
        </span>
      )}
    </li>
  );
}

/** Rate und Turnus gibt es nur zusammen (Server W38-2, PATCH-Regel). */
const RATE_HALB = "Rate und Turnus zusammen angeben.";
/** Grenzen von `RateAb` im Server (`schemas/dokument.py`). */
const RATE_AB_MIN = "1900-01-01";
const RATE_AB_MAX = "2199-12-31";

function Absatz(props: { children: ReactNode }) {
  return <p className="t-footnote mt-2 px-4">{props.children}</p>;
}

/** W47i (WI5): Kennzeichen „geändert“ an einem Abschnitt, dessen Wert sich im neuen Vorschlag geaendert hat. */
function GeaendertChip(props: { an: boolean; className?: string }) {
  return props.an ? (
    <Chip zustand="bald" icon={RefreshCw} className={props.className}>
      geändert
    </Chip>
  ) : null;
}

type RateForm = { betrag: string; turnus: string; ab: string };

/** Startwerte des Formulars aus einem Vorschlag (nur sichere, P4): beim Oeffnen und, fuer nicht angefasste Felder, nach
 *  einem 409 `VORSCHLAG_GEAENDERT` (W47i, WI5). Das Datum aus der Karte (`vorbelegung`, D-079) gilt nur beim Oeffnen. */
type Start = {
  absender: string;
  personId: string | null;
  kategorieId: string;
  datum: string;
  betreff: string;
  dokumentart: string;
  betrag: string;
  rate: RateForm;
  akte: AkteForm;
  fristen: Record<number, boolean>;
};

function startwerte(d: DokumentDetail, v: Vorschlag | null, absenderName: string | undefined, vorbelegtesDatum?: string): Start {
  const schwelle = v?.schwelle ?? STANDARD_SCHWELLE;
  const f: Partial<Vorschlag["felder"]> | undefined = v?.felder;
  const vorbelegt = (feld: KiFeld | undefined, teil: "wert" | "id" = "wert") => (sicher(feld, schwelle) ? String(feld?.[teil] ?? "") : "");
  let personId: string | null = null;
  if (d.person_id) personId = d.person_id;
  else if (d.gemeinsam) personId = GEMEINSAM;
  else if (sicher(f?.person, schwelle)) personId = f?.person?.wert === "gemeinsam" ? GEMEINSAM : String(f?.person?.id ?? "");
  // W38-3 (Plan R-3): Rate nur sicher vorbelegt, alle drei zugleich; sonst die Rate am Brief (nur sicher oder bestaetigt)
  const rate = rateSicher(v) ?? (d.rate_betrag && d.rate_turnus ? { betrag: d.rate_betrag, turnus: d.rate_turnus, ab: d.rate_ab ?? null } : null);
  return {
    absender: vorbelegt(f?.absender) || absenderName || "",
    personId,
    kategorieId: vorbelegt(f?.kategorie, "id") || d.kategorie_id || "",
    datum: vorbelegtesDatum || vorbelegt(datumFeld(f?.datum)) || d.datum || "",
    betreff: vorbelegt(f?.betreff) || d.betreff || "",
    dokumentart: vorbelegt(f?.dokumentart) || d.dokumentart || "",
    betrag: (vorbelegt(f?.betrag) || d.betrag || "").replace(".", ","),
    rate: { betrag: (rate?.betrag ?? "").replace(".", ","), turnus: rate?.turnus ?? "", ab: rate?.ab ?? "" },
    // Akte: bestehende Zuordnung, sonst sicherer Vorschlag, sonst keine; Unsicheres nie (P4, W22d-w3)
    akte: akteVorbelegung({ akten: d.akten, vorschlag: v }),
    fristen: Object.fromEntries((v?.fristen ?? []).map((fr, i) => [i, fr.konfidenz >= schwelle && fristWaehlbar(fr)])),
  };
}

/** W47i (WI5): Auswahl der Fristen eines neuen Vorschlags. Nicht angefasst: die Startwerte des neuen; angefasst: je
 *  Frist die Wahl der Frist gleicher Art und gleichen Datums im alten Vorschlag (nie ueber den Index, die Reihenfolge
 *  kann sich aendern), eine neue Frist bleibt aus. */
function fristenUebertragen(alt: Vorschlag | null, neu: Vorschlag | null, wahl: Record<number, boolean>, start: Record<number, boolean>, neuStart: Record<number, boolean>): Record<number, boolean> {
  const altListe = alt?.fristen ?? [];
  const angefasst = altListe.some((_, i) => Boolean(wahl[i]) !== Boolean(start[i]));
  if (!angefasst) return neuStart;
  return Object.fromEntries(
    (neu?.fristen ?? []).map((fr, j) => {
      const i = altListe.findIndex((a) => a.art === fr.art && a.wert === fr.wert);
      return [j, i >= 0 && Boolean(wahl[i]) && fristWaehlbar(fr)];
    }),
  );
}

const gleich = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** „Bitte pruefen“ abschliessen: Vorschlag uebernehmen oder aendern, dann ablegen. Jede Aenderung
 *  wird serverseitig eine Korrektur (Lernsignal); der Absender lernt eine Haushaltsregel. */
export function PruefenSheet(props: {
  dokument: DokumentDetail;
  offen: boolean;
  onSchliessen: () => void;
  /** In der Vorschlagskarte bewusst uebernommene Werte (D-079): sie gelten hier als vorbelegt. */
  vorbelegung?: { datum?: string };
  /** W81 (S4): nach dem Ablegen mit Kategorie oder Art anders als im gezeigten Vorschlag (leise Zeile der Dokumentansicht;
   *  Posteingang und Kennenlernen geben ihn nicht, F4 A) */
  onKorrigiert?: () => void;
}) {
  const [bereit, setBereit] = useState(false);
  // D-089 (W51): bis zur Antwort schliesst nichts das Blatt
  const sicherung = useSichern(props.offen);
  return (
    <Sheet
      offen={props.offen}
      onSchliessen={props.onSchliessen}
      gesperrt={sicherung.laeuft}
      titel="Prüfen"
      untertitel={props.dokument.vorschlag ? "Vorschlag der Einordnung" : "Selbst einordnen"}
      rechts={
        <button
          type="submit"
          form="pruefen-formular"
          disabled={!bereit}
          className="t-headline min-h-(--touch) text-pg-accent-text disabled:text-pg-text-3"
        >
          Ablegen
        </button>
      }
    >
      {props.offen && (
        <Formular
          dokument={props.dokument}
          vorbelegung={props.vorbelegung}
          onFertig={props.onSchliessen}
          onKorrigiert={props.onKorrigiert}
          onBereit={setBereit}
          sichern={sicherung.sichern}
        />
      )}
    </Sheet>
  );
}

/** Der Zustand entsteht beim Oeffnen aus dem Vorschlag (nur sichere Werte). W47i: der Vorschlag ist der beim Oeffnen
 *  (angeheftet, samt Kennung aus derselben Antwort); ein Neuladen des Briefs aendert ihn nicht, nur ein 409
 *  `VORSCHLAG_GEAENDERT` (WI5: Hinweis oben, nicht angefasste Felder mit den Startwerten des neuen Vorschlags). */
function Formular(props: {
  dokument: DokumentDetail;
  vorbelegung?: { datum?: string };
  onFertig: () => void;
  onKorrigiert?: () => void;
  onBereit: (bereit: boolean) => void;
  sichern: (lauf: SichernLauf) => void;
}) {
  const d = props.dokument;
  const [gezeigt, setGezeigt] = useState(() => ({ vorschlag: d.vorschlag, kennung: "vorschlag_id" in d ? d.vorschlag_id : undefined }));
  const v = gezeigt.vorschlag;
  const zitat = kartenZitat(v);
  // W82 Teil 3 (Punkt 8): unter einer WeltWurzel trägt das Blatt die Gestalt der Vorlage `blatt`
  const welt = useWeltAktiv();
  const schwelle = v?.schwelle ?? STANDARD_SCHWELLE;
  // Verifier W37i-2 R1 A1: jedes Feld des Vorschlags kann fehlen, wie in vorschlagAblage (H6); nie ein Absturz
  const f: Partial<NonNullable<typeof v>["felder"]> | undefined = v?.felder;
  // Briefdatum nur als gueltiges ISO-Datum, sonst nur der Grund (W37c, Verifier R2 A1)
  const fDatum = datumFeld(f?.datum);
  const { data: personen } = usePersonen();
  const { data: kategorien } = useKategorien();
  const namen = useNamen();
  const bestaetigen = useBestaetigen(d.id, 0, v);
  const abgelegtMelden = useAbgelegtMelden();
  // Einmal gesendet ist gesendet: ein zweiter Klick (auch waehrend das Sheet ausblendet) schickt
  // nichts mehr (Verifier W4, B3); erst ein Fehler gibt das Formular wieder frei
  const gesendet = useRef(false);
  const [sperre, setSperre] = useState(false);

  const [anfang] = useState(() => startwerte(d, d.vorschlag, namen.absender(d.absender_id), props.vorbelegung?.datum));
  // W47i: was das Formular zuletzt selbst gesetzt hat; weicht ein Feld davon ab, hat der Nutzer es angefasst
  const start = useRef(anfang);
  const [absender, setAbsender] = useState(anfang.absender);
  // null: noch niemand gewaehlt (auch nicht „Niemand bestimmtes“)
  const [personId, setPersonId] = useState<string | null>(anfang.personId);
  const [kategorieId, setKategorieId] = useState(anfang.kategorieId);
  const [datum, setDatum] = useState(anfang.datum);
  const [betreff, setBetreff] = useState(anfang.betreff);
  const [dokumentart, setDokumentart] = useState(anfang.dokumentart);
  const [betrag, setBetrag] = useState(anfang.betrag);
  const rateAngebot = rateVollstaendig(f?.rate);
  // W38-5 (F44 A): ein erkannter Ratenplan steht als eigene Zeile statt des Hinweises, nie vorbelegt (rateSicher)
  const plan = ratenplanAngebot(v);
  const planId = useId();
  const rateFeld = useRef<HTMLInputElement>(null);
  const [rate, setRate] = useState<RateForm>(anfang.rate);
  const rateSetzen = (teil: Partial<typeof rate>) => setRate((alt) => ({ ...alt, ...teil }));
  const rateVorschlagNehmen = (r: NonNullable<typeof rateAngebot>) => setRate({ betrag: r.betrag.replace(".", ","), turnus: r.turnus, ab: r.ab ?? "" });
  // W82 Web-5c (K13, entscheide E5): die drei Felder nur bei Bedarf. Offen mit erkannter Rate, Ratenplan, gespeicherter Rate
  // oder einem Wert in einem der Felder; sonst an ihrer Stelle „Rate hinzufügen“. Einmal offen bleibt offen, solange das
  // Blatt steht (auch wenn die Felder wieder leer sind); das Senden bleibt (leer heißt keine Rate)
  const rateNoetig = Boolean(rateAngebot || plan || d.rate_betrag || rate.betrag.trim() || rate.turnus || rate.ab);
  const [rateAuf, setRateAuf] = useState(rateNoetig);
  if (rateNoetig && !rateAuf) setRateAuf(true);
  const rateOffen = rateAuf || rateNoetig;
  // nach dem Tipp auf „Rate hinzufügen“ der Fokus in „Rate in Euro“ (der Knopf verschwindet)
  const rateFokus = useRef(false);
  useEffect(() => {
    if (!rateOffen || !rateFokus.current) return;
    rateFokus.current = false;
    rateFeld.current?.focus();
  }, [rateOffen]);
  const { data: akten } = useAkten();
  const akteSicher = Boolean(v && v.akte.konfidenz >= schwelle);
  const [akte, setAkte] = useState<AkteForm>(anfang.akte);
  const akteSetzen = (teil: Partial<AkteForm>) => setAkte((alt) => ({ ...alt, ...teil }));
  const akteNeu = akte.wahl === "neu";
  const akteFertig = !akteNeu || Boolean(akte.name.trim() && akte.art);
  const [eigeneFristen, setEigeneFristen] = useState<{ art: Fristart; datum: string }[]>([]);
  const [fristen, setFristen] = useState<Record<number, boolean>>(anfang.fristen);
  // W47i (WI4, WI5, WI7): Hinweis nach dem 409 mit dem Vorschlag, gegen den verglichen wurde; „Ablegen“ kurz gesperrt
  const [geaendert, setGeaendert] = useState<(VorschlagGeaendert & { vorher: Vorschlag | null; nr: number }) | null>(null);
  const markiert = (s: GeaendertFeld) => Boolean(geaendert?.geaendert?.includes(s));
  const hinweis = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!geaendert) return;
    // der Hinweis steht ganz oben im Formular: bis zum Dialog nach oben scrollen. scrollIntoView reicht nicht, der Kopf
    // des Blatts ist sticky und verdeckte den Anfang (bei 200 % ist der Hinweis hoeher als die Flaeche, W47i E4)
    for (let e = hinweis.current?.parentElement; e; e = e.parentElement) {
      if (e.scrollTop > 0) e.scrollTop = 0;
      if (e.getAttribute("role") === "dialog") break;
    }
    hinweis.current?.focus({ preventScroll: true });
  }, [geaendert]);
  const [kurzGesperrt, setKurzGesperrt] = useState(false);
  const sperrUhr = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(sperrUhr.current), []);

  /** W47i (WI5): nach dem 409 den neuen Vorschlag zeigen. Felder, die der Nutzer angefasst hat (anders als zuletzt vom
   *  Formular gesetzt), bleiben; die anderen bekommen die Startwerte des neuen Vorschlags nach denselben Regeln wie beim
   *  Oeffnen (nur sichere, P4). Eigene Fristen bleiben immer. */
  function neuBelegen(g: VorschlagGeaendert) {
    const alt = start.current;
    const neu = startwerte(d, g.vorschlag, namen.absender(d.absender_id));
    const jetzt: Omit<Start, "fristen"> = { absender, personId, kategorieId, datum, betreff, dokumentart, betrag, rate, akte };
    const behalten = <K extends keyof typeof jetzt>(k: K): (typeof jetzt)[K] => (gleich(jetzt[k], alt[k]) ? neu[k] : jetzt[k]);
    const naechster: Start = {
      absender: behalten("absender"),
      personId: behalten("personId"),
      kategorieId: behalten("kategorieId"),
      datum: behalten("datum"),
      betreff: behalten("betreff"),
      dokumentart: behalten("dokumentart"),
      betrag: behalten("betrag"),
      rate: behalten("rate"),
      akte: behalten("akte"),
      fristen: fristenUebertragen(v, g.vorschlag, fristen, alt.fristen, neu.fristen),
    };
    setAbsender(naechster.absender);
    setPersonId(naechster.personId);
    setKategorieId(naechster.kategorieId);
    setDatum(naechster.datum);
    setBetreff(naechster.betreff);
    setDokumentart(naechster.dokumentart);
    setBetrag(naechster.betrag);
    setRate(naechster.rate);
    setAkte(naechster.akte);
    setFristen(naechster.fristen);
    // angefasste Felder gelten weiter als angefasst: gemerkt wird der Startwert, nicht die Eingabe
    start.current = Object.fromEntries(Object.entries(naechster).map(([k, w]) => [k, k === "fristen" ? neu.fristen : gleich(w, neu[k as keyof Start]) ? w : alt[k as keyof Start]])) as Start;
    setGeaendert({ ...g, vorher: v, nr: (geaendert?.nr ?? 0) + 1 });
    setGezeigt({ vorschlag: g.vorschlag, kennung: g.vorschlag_id });
    setKurzGesperrt(true);
    clearTimeout(sperrUhr.current);
    sperrUhr.current = setTimeout(() => setKurzGesperrt(false), SPERRE_MS);
  }

  const betragWert = betragEingabe(betrag);
  const betragFalsch = betrag.trim() !== "" && betragWert === null;
  // Dokumentart wie der Server: nach dem Trimmen hoechstens 60 Codepunkte, sonst 422 (W22d-w5)
  const artLaenge = zeichen(dokumentart.trim());
  const artZuLang = artLaenge > DOKUMENTART_MAX;
  // Rate wie der Server: Betrag ueber 0 und unter 10^10, mit Turnus; ein Ab nur mit einer Rate
  const rateEingabe = betragEingabe(rate.betrag);
  const rateWert = rateEingabe !== null && Number(rateEingabe) > 0 && Number(rateEingabe) < 1e10 ? rateEingabe : null;
  const rateFalsch = rate.betrag.trim() !== "" && rateWert === null;
  const rateHalb = !rateFalsch && Boolean(rate.betrag.trim()) !== Boolean(rate.turnus);
  const abOhneRate = Boolean(rate.ab) && !rate.betrag.trim() && !rate.turnus;
  // Ab wie der Server (`RateAb`, 1900-01-01 bis 2199-12-31, sonst 422): „01.03.26“ getippt wird im Datumsfeld 0026-03-01
  const abAusserhalb = Boolean(rate.ab) && !abOhneRate && (rate.ab < RATE_AB_MIN || rate.ab > RATE_AB_MAX);
  const rateUebernommen = rateAngebot !== null && rateWert === betragEingabe(rateAngebot.betrag) && rate.turnus === rateAngebot.turnus;
  // W38-5: „übernommen“ nur, wenn alle drei dem Plan gleichen (so nimmt ihn der Server, `plan-w38-5.md` Abschnitt 4); leer ist
  // „offen“, alles andere die eigene Angabe
  const planZustand = !plan ? null : rateUebernommen && rate.ab === (plan.ab ?? "") ? "uebernommen" : !rate.betrag.trim() && !rate.turnus && !rate.ab ? "offen" : "eigene";
  const bereit = Boolean(kategorieId) && !betragFalsch && !artZuLang && akteFertig && !rateFalsch && !rateHalb && !abOhneRate && !abAusserhalb && !sperre && !kurzGesperrt;
  const { onBereit } = props;
  useEffect(() => onBereit(bereit), [bereit, onBereit]);

  const gemeinsam = f?.person?.wert === "gemeinsam";
  const kategorieVorschlag = kategorien?.find((k) => k.id === f?.kategorie?.id);
  const personVorschlag = personen?.find((p) => p.id === f?.person?.id);
  const akteVorschlag = v?.akte.id ? akten?.find((a) => a.id === v.akte.id) : undefined;
  // Gruende, die schon an einem Feld stehen, nicht noch einmal oben. W47c-2 (Verifier R1 H1): den festen Grund zum
  // Wiederoeffnen sagt der Hinweis an „Zuordnen“, solange die vorgeschlagene Akte gewaehlt ist; oben stuende er auch bei
  // einer anderen Akte, die der Server nicht oeffnet
  const feldGruende = new Set([...Object.values(f ?? {}).map((x) => x?.grund), v?.akte.grund, ...(v?.fristen ?? []).map((x) => x.grund)].filter(Boolean));
  // W38-5: den Brief-Grund „Ratenplan erkannt“ sagt die Zeile im Abschnitt „Rate“, solange sie steht
  const allgemein = (v?.gruende ?? []).filter((g) => !feldGruende.has(g) && g !== GRUND_WIEDER_OEFFNEN && !(plan && g === GRUND_RATENPLAN));
  // W82 Web-5b (K14, entscheide B3): Regel B2 des Servers auf den Fristen, wie sie „Ablegen“ schickt (gewählte des Vorschlags
  // und eigene mit Tag; Rate nur mit Turnus, wie gesendet). Trifft sie eine eigene Frist, nennt deren Zeile den Betrag
  const eigeneBetrag = new Map(
    zahlungsbetragVorschau(
      [
        ...(v?.fristen ?? []).filter((fr, i) => fristen[i] && fristWaehlbar(fr)).map((fr) => ({ art: fr.art, betrag: fr.betrag, datum: fr.wert, eigene: -1 })),
        ...eigeneFristen.flatMap((fr, i) => (fr.datum ? [{ art: fr.art, betrag: null, datum: fr.datum, eigene: i }] : [])),
      ],
      betragWert,
      rateWert && rate.turnus ? rateWert : null,
      rate.ab || null,
    ).flatMap((fr) => (fr.eigene >= 0 && fr.betrag ? [[fr.eigene, fr.betrag] as const] : [])),
  );

  function ablegen(e: FormEvent) {
    e.preventDefault();
    if (!bereit || gesendet.current) return;
    gesendet.current = true;
    setSperre(true);
    const gewaehlt = (v?.fristen ?? []).filter((fr, i) => fristen[i] && fristWaehlbar(fr));
    // BS30: nach SICHERN_GRENZE_MS ohne Antwort bricht `useSichern` ab (onError gibt die Sperre frei, der Netz-Satz steht)
    props.sichern((signal) =>
      bestaetigen.mutateAsync(
        {
          signal,
          absender: absender.trim() || null,
          person_id: personId && personId !== GEMEINSAM ? personId : null,
          gemeinsam: personId === GEMEINSAM,
          kategorie_id: kategorieId || null,
          datum: datum || null,
          betreff: betreff.trim() || null,
          dokumentart: dokumentart.trim() || null,
          betrag: betragWert,
          // W38-3: jedes Feld ausdruecklich, wie `betrag`; leer heisst ausdruecklich keine Rate
          rate: rateWert && rate.turnus ? { betrag: rateWert, turnus: rate.turnus as RateTurnus, ab: rate.ab || null } : null,
          ...(akteNeu && akte.art && akte.name.trim()
            ? { akte_neu: { art: akte.art, name: akte.name.trim() } }
            : { akte_id: akte.wahl && !akteNeu ? akte.wahl : null }),
          fristen: [
            ...gewaehlt.map((fr) => ({ art: fr.art, datum: fr.wert as string, betrag: fr.betrag })),
            ...eigeneFristen.filter((fr) => fr.datum).map((fr) => ({ ...fr, betrag: null })),
          ],
          // W47i (WI1): die Kennung aus der Antwort, aus der das Blatt gebaut ist, nur wenn der Server sie nennt
          ...(gezeigt.kennung === undefined ? {} : { vorschlag_id: gezeigt.kennung }),
        },
        {
          onSuccess: (abgelegt, gesendet) => {
            // W81 (S4): Korrektur gegen den gezeigten Vorschlag, nicht gegen den Stand des Briefs; ohne Vorschlag (Selbst
            // einordnen) ist jede gewaehlte Kategorie eine
            if (kategorieId !== (f?.kategorie?.id ?? "") || dokumentart.trim() !== (f?.dokumentart?.wert ?? "").trim()) props.onKorrigiert?.();
            props.onFertig();
            // W82 Web-6b (WN-16): die Fristen genau dieses Auftrags, nicht aus dem Mutation-Cache hergeleitet
            abgelegtMelden(abgelegt, false, gesendet.fristen ?? []);
          },
          onError: (fehler) => {
            gesendet.current = false;
            setSperre(false);
            // W47i (WI5, F2 = A): das Blatt bleibt offen und zeigt den neuen Vorschlag
            const g = vorschlagGeaendert(fehler);
            if (g) neuBelegen(g);
          },
        },
      ),
    );
  }

  return (
    // keine Zeile im Formular nutzt Container-Abfragen; als Container zeichnete Chromium nach dem 409 geaenderte Zeilen leer (R4)
    <form id="pruefen-formular" className="zeilen-ohne-container" onSubmit={ablegen}>
      <fieldset disabled={sperre} className="min-w-0">
        {/* W47i (WI5): oben im Formular, sichtbar gescrollt; bleibt bis zum naechsten Erfolg (dann schliesst das Blatt) */}
        {geaendert && (
          <VorschlagGeaendertHinweis
            ref={hinweis}
            className="mt-4"
            detail={geaendert.detail}
            zeilen={aenderungen(geaendert.vorher, geaendert.vorschlag, geaendert.geaendert, {
              kategorie: (id) => kategorien?.find((k) => k.id === id)?.name,
              person: (id) => personen?.find((p) => p.id === id)?.name,
              akte: (id) => akten?.find((a) => a.id === id)?.name,
            })}
          />
        )}
        {allgemein.length > 0 && (
          <p className="t-footnote mt-4 rounded-lg bg-pg-warn-soft px-4 py-3 text-pg-warn">{allgemein.join(" · ")}</p>
        )}
        {/* W73c (D-280 b): derselbe Auszug wie auf der Pruefkarte (Vorschlagskarte), ohne Auszug der Beleg */}
        {zitat && (
          <p className={`t-footnote mt-4 px-4 text-pg-text-2 italic wrap-anywhere ${welt ? "blatt-zitat" : ""}`} data-testid="pruefen.auszug">
            {/* W82 (Vorlage `blatt` `.blatt-zitat`): Anführungszeichen in funke vor dem Zitat, nur Bild */}
            {welt && <Quote size={12} strokeWidth={2.5} aria-hidden />}
            {welt ? <span className="min-w-0">„{zitat}“</span> : <>„{zitat}“</>}
          </p>
        )}

        <h3 className="t-caption-1 t-caption-1-medium mt-6 mb-2 px-4 text-pg-text-2">
          Kategorie <GeaendertChip an={markiert("kategorie")} className="ml-1" />
        </h3>
        {/* Handoff PgDocument zeigt ein Auswahlfeld; wir behalten die Auswahl als Chips (ein Tipp, alle
            sichtbar, Radiogruppe wie bisher), ohne die bunten Kacheln (Plan 6.7) */}
        <div role="radiogroup" aria-label="Kategorie" onKeyDown={chipTaste} className="wahl-chips flex flex-wrap gap-2 px-1">
          {(kategorien ?? []).map((k, i, alle) => {
            const aktiv = k.id === kategorieId;
            return (
              <button
                key={k.id}
                type="button"
                role="radio"
                aria-checked={aktiv}
                tabIndex={chipTab(aktiv, !alle.some((x) => x.id === kategorieId), i)}
                onClick={() => setKategorieId(k.id)}
                className={`wahl-chip t-footnote inline-flex min-h-(--touch) max-w-full items-center gap-1 rounded-md pr-3 pl-1 ${aktiv ? "bg-pg-accent-soft font-semibold text-pg-accent-text" : "bg-pg-surface text-pg-text shadow-(--pg-feld-rand) hover:bg-pg-surface-2"}`}
              >
                {aktiv ? (
                  <span className="grid size-(--size-tile) shrink-0 place-items-center">
                    <Check size={18} strokeWidth={2} aria-hidden />
                  </span>
                ) : welt ? (
                  // W82 (Vorlage `.wahl-chip`): das Symbol der Kategorie ohne Kachel
                  createElement(kategorieSymbol(k.schluessel), { size: 18, strokeWidth: 1.9, "aria-hidden": true })
                ) : (
                  <Kachel schluessel={k.schluessel} />
                )}
                <span className="[overflow-wrap:anywhere]">{k.name}</span>
              </button>
            );
          })}
        </div>
        <Absatz>
          <Hinweis
            feld={f?.kategorie}
            schwelle={schwelle}
            anzeige={kategorieVorschlag?.name}
            uebernommen={kategorieId === f?.kategorie?.id}
            leer={!kategorieId}
            onUebernehmen={f?.kategorie?.id ? () => setKategorieId(String(f?.kategorie?.id)) : undefined}
          />
        </Absatz>

        <h3 className="t-caption-1 t-caption-1-medium mt-6 mb-2 px-4 text-pg-text-2">
          Für wen <GeaendertChip an={markiert("person")} className="ml-1" />
        </h3>
        <div role="radiogroup" aria-label="Person" onKeyDown={chipTaste} className="wahl-chips flex flex-wrap gap-2 px-1">
          {[...(personen ?? []).map((p) => ({ id: p.id, name: p.name })), { id: GEMEINSAM, name: "Gemeinsam" }, { id: "", name: "Niemand bestimmtes" }].map((p, i, alle) => {
            const aktiv = p.id === personId;
            return (
              <button
                key={p.id || "keiner"}
                type="button"
                role="radio"
                aria-checked={aktiv}
                tabIndex={chipTab(aktiv, !alle.some((x) => x.id === personId), i)}
                onClick={() => setPersonId(p.id)}
                className={`wahl-chip t-footnote inline-flex min-h-(--touch) items-center gap-1 rounded-md px-3 ${aktiv ? "bg-pg-accent-soft font-semibold text-pg-accent-text" : "bg-pg-surface text-pg-text shadow-(--pg-feld-rand) hover:bg-pg-surface-2"}`}
              >
                {aktiv && <Check size={16} strokeWidth={1.75} aria-hidden />}
                {p.name}
              </button>
            );
          })}
        </div>
        <Absatz>
          <Hinweis
            feld={f?.person}
            schwelle={schwelle}
            anzeige={gemeinsam ? "Gemeinsam" : personVorschlag?.name}
            uebernommen={gemeinsam ? personId === GEMEINSAM : personId === f?.person?.id}
            leer={personId === null}
            onUebernehmen={gemeinsam ? () => setPersonId(GEMEINSAM) : f?.person?.id ? () => setPersonId(String(f?.person?.id)) : undefined}
          />
        </Absatz>

        <ul className="gruppe mt-6 overflow-clip rounded-group bg-pg-surface">
          <Feld
            label="Absender"
            value={absender}
            onChange={(e) => setAbsender(e.target.value)}
            maxLength={200}
            hinweis={
              <>
                <GeaendertChip an={markiert("absender")} />
                <Hinweis feld={f?.absender} schwelle={schwelle} uebernommen={absender === f?.absender?.wert} leer={!absender.trim()} onUebernehmen={() => setAbsender(String(f?.absender?.wert ?? ""))} />
              </>
            }
          />
          <Feld
            label="Briefdatum"
            type="date"
            value={datum}
            onChange={(e) => setDatum(e.target.value)}
            hinweis={
              <>
                <GeaendertChip an={markiert("datum")} />
                <Hinweis feld={fDatum} schwelle={schwelle} anzeige={datumText(fDatum?.wert)} uebernommen={datum === fDatum?.wert} leer={!datum} onUebernehmen={() => setDatum(String(fDatum?.wert ?? ""))} />
              </>
            }
          />
          <Feld
            label="Betreff"
            value={betreff}
            onChange={(e) => setBetreff(e.target.value)}
            maxLength={500}
            hinweis={
              <>
                <GeaendertChip an={markiert("betreff")} />
                <Hinweis feld={f?.betreff} schwelle={schwelle} uebernommen={betreff === f?.betreff?.wert} leer={!betreff.trim()} onUebernehmen={() => setBetreff(String(f?.betreff?.wert ?? ""))} />
              </>
            }
          />
          <Feld
            label="Dokumentart"
            value={dokumentart}
            // Kein natives maxLength: das zaehlt UTF-16-Einheiten, der Server Codepunkte (W22d-w5)
            onChange={(e) => setDokumentart(hoechstens(e.target.value, DOKUMENTART_MAX))}
            data-testid="pruefen-dokumentart"
            aria-invalid={artZuLang || undefined}
            hinweis={
              <>
                <GeaendertChip an={markiert("dokumentart")} />
                <Hinweis
                  feld={f?.dokumentart}
                  schwelle={schwelle}
                  uebernommen={dokumentart === f?.dokumentart?.wert}
                  leer={!dokumentart.trim()}
                  onUebernehmen={() => setDokumentart(String(f?.dokumentart?.wert ?? ""))}
                />
                {artLaenge >= DOKUMENTART_MAX - 10 && (
                  <span data-testid="pruefen-dokumentart-laenge" className={`block t-zahl ${artZuLang ? "text-pg-danger" : "text-pg-text-2"}`}>
                    {artZuLang ? `Höchstens ${DOKUMENTART_MAX} Zeichen, jetzt ${artLaenge}.` : `${artLaenge} von höchstens ${DOKUMENTART_MAX} Zeichen`}
                  </span>
                )}
              </>
            }
          />
          <Feld
            label="Betrag in Euro"
            inputMode="decimal"
            value={betrag}
            onChange={(e) => setBetrag(e.target.value)}
            className="t-zahl"
            aria-invalid={betragFalsch || undefined}
            hinweis={
              <>
                <GeaendertChip an={markiert("betrag")} />
                {betragFalsch ? (
                  <span className="text-pg-danger">Bitte als Betrag eingeben, etwa 1.290,50.</span>
                ) : (
                  <Hinweis
                    feld={f?.betrag}
                    schwelle={schwelle}
                    anzeige={f?.betrag?.wert?.replace(".", ",")}
                    uebernommen={betragWert === f?.betrag?.wert}
                    leer={!betrag.trim()}
                    onUebernehmen={() => setBetrag(String(f?.betrag?.wert ?? "").replace(".", ","))}
                  />
                )}
              </>
            }
          />
        </ul>

        {rateOffen ? (
          <>
            {/* W38-3 (Plan R-3): Rate je Zeitraum getrennt vom faelligen Betrag (D-080); Hinweise nur in den Zeilen */}
            <h3 className="t-caption-1 t-caption-1-medium mt-6 mb-2 px-4 text-pg-text-2">
              Rate <GeaendertChip an={markiert("rate")} className="ml-1" />
            </h3>
            <ul className="gruppe overflow-clip rounded-group bg-pg-surface">
              {/* W38-5 (F44a A): der erkannte Plan als eigene Zeile vor „Rate in Euro“ statt des Hinweises (Gestaltung wie der Hinweis
                  zur geschlossenen Akte, Knopf sekundaer); „Übernehmen“ setzt alle drei Felder wie der Vorschlag. Die Zeile ist die
                  Beschreibung von „Rate in Euro“ (W52b), „übernommen“ sagt der Status einmal an */}
              {plan && planZustand && (
                <li className="zeile zeile-ohne-container relative flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3" data-testid="pruefen.ratenplan">
                  {/* bei 320 px und 200 % bleiben dem Text 160 px: Silben trennen statt ueber den Rand („übernehmen?“) oder mitten
                      im Wort brechen (Zitat „monatliche|n“) */}
                  <p id={planId} className="t-footnote min-w-0 flex-1 basis-48 hyphens-auto">
                    {/* steht immer, damit die Ansage sicher kommt; leer ohne Hoehe */}
                    <span role="status" className="block text-pg-ok">
                      {planZustand === "uebernommen" && (
                        <>
                          <Check size={16} strokeWidth={2} aria-hidden className="mr-1 inline-block align-text-bottom" />
                          {ratenplanSatz(plan, "uebernommen")}
                        </>
                      )}
                    </span>
                    {planZustand !== "uebernommen" && <span className="block text-pg-text">{ratenplanSatz(plan, planZustand)}</span>}
                    {f?.rate?.beleg && (
                      <span className="mt-1 block">
                        <Zitat beleg={f.rate.beleg} />
                      </span>
                    )}
                  </p>
                  {planZustand !== "uebernommen" && (
                    <Knopf
                      art="sekundaer"
                      // wie „Übernehmen“ beim Briefdatum der Karte: bei 320 px und 200 % getrennt statt ueber den Rand (W37c H5)
                      className="hyphens-auto"
                      aria-label={ratenplanKnopf(plan, planZustand)}
                      onClick={() => {
                        rateVorschlagNehmen(plan);
                        // der Knopf verschwindet: der Fokus geht an „Rate in Euro“, die Zeile ist dessen Beschreibung
                        rateFeld.current?.focus();
                      }}
                    >
                      {planZustand === "eigene" ? "Vorschlag nehmen" : "Übernehmen"}
                    </Knopf>
                  )}
                </li>
              )}
              <Feld
                ref={rateFeld}
                label="Rate in Euro"
                inputMode="decimal"
                value={rate.betrag}
                onChange={(e) => rateSetzen({ betrag: e.target.value })}
                className="t-zahl"
                aria-invalid={rateFalsch || (rateHalb && !rate.betrag.trim()) || undefined}
                aria-describedby={plan ? planId : undefined}
                hinweis={
                  <>
                    {rateFalsch && <span className="block text-pg-danger">Bitte als Betrag eingeben, etwa 21,05.</span>}
                    {rateHalb && !rate.betrag.trim() && <span className="block text-pg-danger">{RATE_HALB}</span>}
                    {!plan && (
                      <Hinweis
                        // ohne vollstaendige Rate kein Vorschlag, nur der Grund (wie das Briefdatum)
                        feld={f?.rate && (rateAngebot ? f.rate : { ...f.rate, wert: null })}
                        schwelle={schwelle}
                        anzeige={rateText(rateAngebot?.betrag, rateAngebot?.turnus) ?? undefined}
                        uebernommen={rateUebernommen}
                        leer={!rate.betrag.trim() && !rate.turnus}
                        onUebernehmen={rateAngebot ? () => rateVorschlagNehmen(rateAngebot) : undefined}
                      />
                    )}
                  </>
                }
              />
              <Auswahl
                label="Turnus"
                value={rate.turnus}
                onChange={(e) => rateSetzen({ turnus: e.target.value })}
                aria-invalid={(rateHalb && Boolean(rate.betrag.trim())) || undefined}
                hinweis={rateHalb && rate.betrag.trim() ? <span className="text-pg-danger">{RATE_HALB}</span> : undefined}
              >
                <option value="">Kein Turnus</option>
                {Object.entries(TURNUS_JE).map(([wert, name]) => (
                  <option key={wert} value={wert}>
                    {name}
                  </option>
                ))}
              </Auswahl>
              <Feld
                label="Ab"
                type="date"
                value={rate.ab}
                onChange={(e) => rateSetzen({ ab: e.target.value })}
                className="t-zahl"
                min={RATE_AB_MIN}
                max={RATE_AB_MAX}
                aria-invalid={abOhneRate || abAusserhalb || undefined}
                hinweis={
                  abOhneRate ? (
                    <span className="text-pg-danger">Ein Startdatum gibt es nur mit einer Rate.</span>
                  ) : abAusserhalb ? (
                    <span className="text-pg-danger">Bitte ein Datum zwischen 1900 und 2199 angeben.</span>
                  ) : undefined
                }
              />
            </ul>
          </>
        ) : (
          // W82 Web-5c (K13): leiser Knopf an der Stelle des Blocks; bei 320 px und 200 % bricht er um statt zu ragen
          <div className="pruefen-rate-hinzu mt-6 px-4">
            <Knopf
              art="sekundaer"
              icon={Plus}
              className="hyphens-auto"
              data-testid="pruefen.rate-hinzufuegen"
              onClick={() => {
                rateFokus.current = true;
                setRateAuf(true);
              }}
            >
              Rate hinzufügen
            </Knopf>
          </div>
        )}

        <h3 className="t-caption-1 t-caption-1-medium mt-6 mb-2 px-4 text-pg-text-2">
          Akte <GeaendertChip an={markiert("akte")} className="ml-1" />
        </h3>
        <ul className="gruppe overflow-clip rounded-group bg-pg-surface">
          {/* Beschriftung ueber der Auswahl wie „Turnus“: links daneben mit fester Breite (w-28) blieb der Auswahl bei 200 %
              0 px (Verifier W38-3 R1, A3) */}
          {/* W47c-2: der Hinweis nur bei der vorgeschlagenen Akte; eine andere geschlossene oeffnet der Server nicht */}
          <Auswahl
            // W82 Web-6a (WN-20): Schrift bei schmalem Fenster und großer Schrift wie das Tippfeld (bausteine.css, D-739)
            data-aktenwahl=""
            label="Zuordnen"
            value={akte.wahl}
            onChange={(e) => akteSetzen({ wahl: e.target.value })}
            hinweis={akteWiederOeffnen(v, akte.wahl, akten?.find((a) => a.id === akte.wahl)?.status) ? "Diese Akte ist geschlossen und wird beim Ablegen wieder geöffnet." : undefined}
          >
            <option value="">Keine Akte</option>
            {(akten ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
            <option value="neu">Neue Akte …</option>
          </Auswahl>
          {akteNeu && (
            <>
              <Feld label="Name der Akte" value={akte.name} onChange={(e) => akteSetzen({ name: e.target.value })} maxLength={200} required />
              <li className="zeile relative px-4 py-3">
                {/* Ohne Standard: keine Art gewaehlt heisst „Bitte waehlen“ (P4, W22d-w3) */}
                <Segment
                  label="Art der Akte"
                  wert={akte.art}
                  onChange={(art) => akteSetzen({ art })}
                  optionen={(["vertrag", "versicherung", "forderung", "sonstige"] as const).map((art) => ({ wert: art, label: AKTENARTEN[art] ?? art }))}
                />
                {!akte.art && <p className="t-footnote mt-2 text-pg-text-2">Art der Akte: bitte wählen</p>}
                {/* W47e-2: der Hinweis steht in dieser Zeile, nicht als Absatz unter den Gruppen: erschien er dort zugleich mit der
                    Wahl „Neue Akte …“, legte Chromium die Auswahl „Zuordnen“ nicht mehr an (0 px, nicht bedienbar; wie W47e E4) */}
                {!akteFertig && <p className="t-footnote mt-2 text-pg-text-2">Zum Ablegen bitte Name und Art der neuen Akte angeben.</p>}
              </li>
            </>
          )}
        </ul>
        {v && (v.akte.id || v.akte.neu_name) && (
          <Absatz>
            {akteEntspricht(v.akte, akte) ? (
              akteSicher && (
                <span className="text-pg-text-2">
                  {v.akte.neu_name ? "Vorschlag der Einordnung; Briefe mit denselben Kennungen landen später von selbst darin." : <Zitat beleg={v.akte.beleg} />}
                </span>
              )
            ) : (
              // Der Hinweis bleibt, bis Wahl, Name und Art dem Vorschlag entsprechen (W22d-w3)
              <span className={`flex flex-wrap items-center gap-x-2 ${akteSicher ? "text-pg-text-2" : "text-pg-warn"}`}>
                <span>
                  Vorschlag:{" "}
                  {v.akte.id
                    ? `„${akteVorschlag?.name ?? "Akte"}“`
                    : `neue Akte „${v.akte.neu_name}“${v.akte.neu_art ? ` (${AKTENARTEN[v.akte.neu_art] ?? v.akte.neu_art})` : ""}`}
                  , Akte {sicherheit(v.akte.konfidenz, v.akte.grund, schwelle)}
                </span>
                <button type="button" onClick={() => setAkte((alt) => akteUebernehmen(v.akte, alt))} className="min-h-(--touch) font-semibold text-pg-accent-text hover:opacity-70">
                  Übernehmen
                </button>
              </span>
            )}
          </Absatz>
        )}

        {(v?.fristen.length ?? 0) > 0 && (
          <>
            <h3 className="t-caption-1 t-caption-1-medium mt-6 mb-2 px-4 text-pg-text-2">
              Fristen aus dem Brief <GeaendertChip an={markiert("fristen")} className="ml-1" />
            </h3>
            {/* W73d: bei grosser Schrift weniger Innenrand (bausteine.css, .fristen-gruppe) */}
            <ul className="fristen-gruppe gruppe overflow-clip rounded-group bg-pg-surface">
              {v?.fristen.map((fr, i) => (
                <li key={`${fr.art}-${fr.wert}-${i}`} className="zeile relative px-4 py-1">
                  <Schalter
                    an={Boolean(fristen[i])}
                    // gesperrt auch bei einem Wert, der kein ISO-Datum ist (W37c, Verifier R2 A1)
                    disabled={!fristWaehlbar(fr)}
                    onChange={(an) => setFristen((alt) => ({ ...alt, [i]: an }))}
                    hinweis={
                      <>
                        {fr.konfidenz < schwelle && <span className="block text-pg-warn">{fr.konfidenz === 0 ? fr.grund : `Unsicher: ${grundHalbsatz(fr.grund) || "geringe Sicherheit"}`}</span>}
                        {/* W82 Web-8 W8-3 (W88 NW-1): Kennzeichen der abgeleiteten Frist, Text des Servers und wovon aus gezählt */}
                        {fr.abgeleitet && (
                          <span className="block" data-abgeleitet="">
                            {fr.abgeleitet.hinweis} · {abgeleitetBasis(fr.abgeleitet)}
                          </span>
                        )}
                        <Zitat beleg={fr.beleg} />
                      </>
                    }
                  >
                    {/* das Datum bricht nie in sich (D-073). W82-3 R1 (FW12 „wie iOS“, `Format.fristart`): das Fristwort
                        „Zahlung bis …“ statt „Zahlen bis …“ */}
                    {FRISTWORT[fr.art] ?? FRISTWORT.sonstige} bis <span className="t-zahl whitespace-nowrap">{datumText(fr.wert)}</span>
                  </Schalter>
                </li>
              ))}
            </ul>
          </>
        )}

        <h3 className="t-caption-1 t-caption-1-medium mt-6 mb-2 px-4 text-pg-text-2">Eigene Fristen</h3>
        <ul className="gruppe overflow-clip rounded-group bg-pg-surface">
          {eigeneFristen.map((fr, i) => (
            <li key={i} className="zeile relative flex min-h-(--touch) flex-wrap items-center gap-3 px-4 py-2">
              <select
                aria-label="Art der Frist"
                aria-describedby={eigeneBetrag.has(i) ? `${planId}-frist-${i}` : undefined}
                value={fr.art}
                onChange={(e) => setEigeneFristen((alt) => alt.map((x, j) => (j === i ? { ...x, art: e.target.value as Fristart } : x)))}
                // W82 Web-5h2 (V3): so breit wie die gewählte Art, nie breiter als die Zeile (bei 320 px und 200 % ragte sie 14 px hinaus)
                className="t-body min-h-(--touch) max-w-full min-w-0 bg-transparent text-pg-text outline-none [field-sizing:content] focus-visible:outline-2 focus-visible:outline-pg-focus"
              >
                {Object.entries(FRISTARTEN).map(([wert, name]) => (
                  <option key={wert} value={wert}>
                    {name}
                  </option>
                ))}
              </select>
              {/* W82 Web-5h-N1 Punkt 3 (R2 5h-W2 F2): Datum und Papierkorb brechen gemeinsam um, nie steht der Papierkorb allein in
                  der zweiten Zeile; die Gruppe ist so breit wie beide nebeneinander (`basis-[max-content]`), passt das nicht einmal in
                  eine eigene Zeile (320 px, 200 %), schrumpft sie und bricht in sich um, statt über den Rand zu ragen */}
              <div className="flex max-w-full min-w-0 grow basis-[max-content] flex-wrap items-center gap-3">
                <DatumFeld
                  aria-label="Frist bis"
                  aria-describedby={eigeneBetrag.has(i) ? `${planId}-frist-${i}` : undefined}
                  value={fr.datum}
                  onChange={(e) => setEigeneFristen((alt) => alt.map((x, j) => (j === i ? { ...x, datum: e.target.value } : x)))}
                  className="t-zahl"
                />
                <button
                  type="button"
                  aria-label="Frist entfernen"
                  onClick={() => setEigeneFristen((alt) => alt.filter((_, j) => j !== i))}
                  className="grid size-(--touch) place-items-center text-pg-danger"
                >
                  <Trash2 size={18} strokeWidth={1.75} aria-hidden />
                </button>
              </div>
              {/* K14: was das Fristen-Modul nach dem Ablegen zählt (Betrag nach format.ts) */}
              {eigeneBetrag.has(i) && (
                <p id={`${planId}-frist-${i}`} className="frist-betrag t-footnote w-full pb-1 text-pg-text-2" data-testid="frist-betrag">
                  Betrag: <span className="t-zahl whitespace-nowrap">{betragText(eigeneBetrag.get(i))}</span>
                </p>
              )}
            </li>
          ))}
          <li className="zeile relative">
            <button
              type="button"
              onClick={() => setEigeneFristen((alt) => [...alt, { art: "zahlen", datum: "" }])}
              className="t-body flex min-h-(--touch) w-full items-center gap-2 px-4 text-pg-accent-text hover:bg-pg-surface-2"
            >
              <Plus size={18} strokeWidth={1.75} aria-hidden />
              {/* W51 (H1): feste Trennstelle (weiches Trennzeichen), bei 320 px und 200 % ragte „hinzufügen“ 1 bis 2 px
                  ueber den Rand; der Name des Knopfs bleibt „Frist hinzufügen“ */}
              {"Frist hinzu\u00ADfügen"}
            </button>
          </li>
        </ul>

        {/* ein 409 VORSCHLAG_GEAENDERT steht oben als Hinweis, nicht als Fehler (WI5) */}
        {bestaetigen.error && !vorschlagGeaendert(bestaetigen.error) && <Fehlertext fehler={bestaetigen.error} />}
        {!kategorieId && <p className="t-footnote mt-6 px-4 text-pg-text-2">Zum Ablegen bitte eine Kategorie wählen.</p>}
        <Knopf type="submit" gross breit className="mt-8" disabled={!bereit}>
          Ablegen
        </Knopf>
      </fieldset>
    </form>
  );
}
